import React from 'react';
import { colors, fonts, sectionLabelStyle, gradients } from '../../patientintel/styles';

// Visual treatment mirrors patientintel/ComparisonSection. Copy is sourced
// from GSK_COPY_FOR_REVIEW.md [DIFFERENT] — 4 competitor columns, 6 rows.

const columns = ['Advisory Boards', 'Bespoke Research', 'Social Listening', 'Pulse Check™'];

const rows = [
  ['Speed to results', 'Weeks–months', 'Months', 'Weeks', 'Days'],
  ['Verified patients', 'Yes *', 'Yes *', 'No', 'Yes'],
  ['Sample breadth', 'Small', 'Small', 'Large', 'Growing'],
  ['Longitudinal signal', 'Limited', 'No', 'Limited', 'Yes'],
  ['Contextual depth', 'High', 'High', 'Low', 'High'],
  ['On-demand querying', 'No', 'No', 'No', 'Yes'],
];

export const DifferentSection = () => {
  const hiIndex = columns.length - 1;
  return (
    <section
      style={{
        backgroundColor: colors.offWhite,
        padding: '96px 48px',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .gsk-diff-wrap { padding-left: 24px !important; padding-right: 24px !important; padding-top: 64px !important; padding-bottom: 64px !important; }
        }
        .gsk-diff-table tr:hover td { background: rgba(235,160,155,0.04); }
        .gsk-diff-table tr:hover td.gsk-hi { background: linear-gradient(135deg, rgba(250,190,192,0.32) 0%, rgba(245,215,170,0.26) 50%, rgba(181,201,168,0.20) 100%); }
        .gsk-br-md { display: none; }
        @media (min-width: 768px) { .gsk-br-md { display: inline; } }
      `}</style>
      <div className="gsk-diff-wrap" style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto 56px', textAlign: 'center' }}>
          <p style={{ ...sectionLabelStyle, textAlign: 'center' }}>Why It's Different</p>
          <h2
            style={{
              fontFamily: fonts.serif,
              fontSize: 'clamp(26px, 3vw, 40px)',
              fontWeight: 700,
              color: colors.charcoal,
              margin: '0 0 16px',
              lineHeight: 1.2,
            }}
          >
            A different category
            <br />
            <span style={{ fontStyle: 'italic', fontWeight: 400 }}>of capability.</span>
          </h2>
          <p
            style={{
              fontFamily: fonts.sans,
              fontSize: '17px',
              color: colors.warmGray,
              maxWidth: '620px',
              margin: '0 auto',
              fontWeight: 400,
              lineHeight: 1.65,
              textWrap: 'pretty',
            }}
          >
            Pulse Checks aren't a faster version of what you already have. And neither is Real Patients. We're designed to sit alongside your existing research model — filling a gap nothing else fills.
          </p>
        </div>

        <div
          style={{
            overflowX: 'auto',
            backgroundColor: colors.white,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`,
            boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
          }}
        >
          <table
            className="gsk-diff-table"
            style={{
              width: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              fontFamily: fonts.sans,
              fontSize: '14px',
              minWidth: '640px',
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                    backgroundColor: colors.lightGray,
                    color: colors.warmGray,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                />
                {columns.map((col, i) => {
                  const hi = i === hiIndex;
                  return (
                    <th
                      key={col}
                      style={{
                        padding: hi ? '18px 16px' : '16px',
                        textAlign: 'center',
                        fontSize: hi ? '13px' : '12px',
                        fontWeight: hi ? 700 : 600,
                        letterSpacing: '0.3px',
                        background: hi ? gradients.primary : colors.lightGray,
                        color: hi ? colors.charcoal : colors.warmGray,
                        borderBottom: hi ? 'none' : `1px solid ${colors.border}`,
                        borderLeft: hi ? `2px solid ${colors.coral}` : 'none',
                        borderRight: hi ? `2px solid ${colors.coral}` : 'none',
                        position: 'relative',
                      }}
                    >
                      {hi && (
                        <span
                          aria-hidden="true"
                          style={{
                            display: 'inline-block',
                            marginRight: '6px',
                            fontSize: '14px',
                            transform: 'translateY(-1px)',
                          }}
                        >
                          ✦
                        </span>
                      )}
                      {col}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIdx) => {
                const isLast = rowIdx === rows.length - 1;
                return (
                  <tr key={row[0]}>
                    <td
                      style={{
                        padding: '14px 16px',
                        textAlign: 'left',
                        color: colors.charcoal,
                        fontWeight: 500,
                        fontSize: '13px',
                        borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
                      }}
                    >
                      {row[0]}
                    </td>
                    {row.slice(1).map((cell, i) => {
                      const hi = i === hiIndex;
                      return (
                        <td
                          key={i}
                          className={hi ? 'gsk-hi' : ''}
                          style={{
                            padding: '14px 16px',
                            textAlign: 'center',
                            borderBottom: isLast ? 'none' : `1px solid ${colors.border}`,
                            background: hi
                              ? 'linear-gradient(135deg, rgba(250,190,192,0.22) 0%, rgba(245,215,170,0.18) 50%, rgba(181,201,168,0.14) 100%)'
                              : 'transparent',
                            color: hi ? colors.charcoal : colors.warmGray,
                            fontWeight: hi ? 600 : 400,
                            borderLeft: hi ? `2px solid ${colors.coral}` : 'none',
                            borderRight: hi ? `2px solid ${colors.coral}` : 'none',
                          }}
                        >
                          {cell}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p
          style={{
            fontFamily: fonts.sans,
            fontSize: '12px',
            color: colors.midGray,
            marginTop: '16px',
            fontStyle: 'italic',
            fontWeight: 400,
          }}
        >
          * Not all vendors verify patient identity and diagnosis.
        </p>
      </div>
    </section>
  );
};
